import React, {
    createContext,
    useState,
    useContext,
    ReactNode,
    useCallback,
    useMemo
} from "react";
import { Constants } from "../constants/appConstants";
import {
    getProviderTypeFromSession,
    saveProviderTypeToSession,
    getReviewYearFromSession,
    saveReviewYearToSession
} from "../utils/sessionStorageHelper";

export interface ProviderTypeOption {
    providerType: string;
    providerCode: string;
}

interface AppContextProps {
    reviewYear: number | undefined;
    setReviewYear: (year: number | undefined) => void;
    providerType: string;
    setProviderType: (type: string) => void;
    providerTypes: ProviderTypeOption[];
    setProviderTypes: (types: ProviderTypeOption[]) => void;
    isLoadingProviderTypes: boolean;
    setIsLoadingProviderTypes: (isLoading: boolean) => void;
}

export const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [reviewYear, setReviewYearState] = useState<number | undefined>(
        () => getReviewYearFromSession() ?? undefined
    );

    const [providerType, setProviderTypeState] = useState<string>(
        () => getProviderTypeFromSession() || Constants.RSKey
    );

    const [providerTypes, setProviderTypes] = useState<ProviderTypeOption[]>([]);
    const [isLoadingProviderTypes, setIsLoadingProviderTypes] = useState<boolean>(false);

    const setProviderType = useCallback((type: string) => {
        setProviderTypeState((currentProviderType) => {
            if (type !== currentProviderType) {
                setReviewYearState(undefined);
            }

            return type;
        });

        saveProviderTypeToSession(type);
    }, []);

    const setReviewYear = useCallback((year: number | undefined) => {
        setReviewYearState(year);

        if (year !== undefined) {
            saveReviewYearToSession(year);
        }
    }, []);

    const value = useMemo(
        () => ({
            reviewYear,
            setReviewYear,
            providerType,
            setProviderType,
            providerTypes,
            setProviderTypes,
            isLoadingProviderTypes,
            setIsLoadingProviderTypes,
        }),
        [
            reviewYear,
            setReviewYear,
            providerType,
            setProviderType,
            providerTypes,
            isLoadingProviderTypes
        ]
    );

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): AppContextProps => {
    const context = useContext(AppContext);

    if (!context) {
        throw new Error("useAppContext must be used within an AppContextProvider");
    }

    return context;
};
